import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import EloBadge from "../common/EloBadge.js";
import StarIcon from "@mui/icons-material/Star";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useHistory } from "react-router-dom";
import utils from "../../universal/utils";
import {
  pickAvatarUpdatedAtMs,
  resolveAvatarSrc,
} from "../../utils/avatarUrl";
import Chip from "@mui/material/Chip";
import Rating3Badge from "../common/Rating3Badge";
import ImpactRatingBadge from "../common/ImpactRatingBadge";
import { FOUNDER_BADGE, FOUNDER_TOOLTIP_TEXT } from "../../constants/founder";
import { TIER_DISPLAY, TIERS } from "../../constants/membership";

const basePath = utils.getEdgeBasePath();

const useStyles = makeStyles((theme) => ({
  flipPlayerBox: {
    width: '100%',
    aspectRatio: '3 / 4.35',
    cursor: 'default',
    transition: 'all 0.15s ease-in-out',
  },
  flipInner: {
    position: 'relative',
    width: '100%',
    height: '100%',
    transition: 'transform 0.8s',
    transformStyle: 'preserve-3d',
  },
  rotate: {
    transform: 'rotateY(180deg)',
  },
  flipCardFront: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    WebkitBackfaceVisibility: 'hidden',
    backfaceVisibility: 'hidden',
  },
  flipCardBack: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    WebkitBackfaceVisibility: 'hidden',
    backfaceVisibility: 'hidden',
    transform: 'rotateY(180deg)',
    cursor: 'default',
  },
  peNone: {
    pointerEvents: 'none',
  },
  closeFlipButton: {
    position: 'absolute',
    top: '0.4rem',
    right: '0.4rem',
    color: '#ffffff',
    backgroundColor: 'rgba(0,0,0,0.25)',
    transition: 'all 0.3s ease',
    width: '1.5rem',
    height: '1.5rem',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.55)', 
      boxShadow: '0 0 0 1px #E8A33D',
    },
    zIndex: 2,
    [theme.breakpoints.up('xl')]: {
      top: '0.6rem',
      right: '0.6rem',
      width: '2rem',
      height: '2rem',
      minWidth: '2rem',
    },
  },
  closeFlipIcon: {
    fontSize: '1.1rem',
    [theme.breakpoints.up('xl')]: {
      fontSize: '1.4rem',
    },
  },
  playerCard: {
    width: '100%',
    height: '100%',
    borderRadius: 4,
    position: 'relative',
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    backgroundColor: '#1C1E20',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.42rem',
    overflow: 'hidden',
    padding: '0.85rem 0.65rem 1.35rem',
    boxSizing: 'border-box',
    cursor: 'pointer',
    [theme.breakpoints.up('xl')]: {
      padding: '1rem 0.85rem 1.55rem',
      gap: '0.52rem',
    },
  },
  playerCardSelf: {
    width: '100%',
    height: 'auto',
    aspectRatio: '3 / 4.35',
    cursor: 'default',
  },
  hoverNotYouCard: {
    '&:hover': {
      borderColor: '#E8A33D',
      boxShadow: '0 4px 16px rgba(232, 163, 61, 0.15)',
    },
  },
  isYouBorder: {
    borderTop: '2px solid #E8A33D',
  },
  searchingDivLayerIsYou: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1C1E2070',
    zIndex: 10,
  },
  playerName: {
    color: '#ffffff',
    fontSize: '1.05rem',
    fontWeight: 700,
    [theme.breakpoints.up('xl')]: {
      fontSize: '1.3rem',
    },
    cursor: 'default',
    textAlign: 'center',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
    letterSpacing: '0.4px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.25rem',
    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    flexShrink: 0,
  },
  tooltipContent: {
    marginTop: "-3rem",
    backgroundColor: "#1C1E20",
    padding: "0.25rem 0.5rem",
    borderRadius: "4px",
    border: "1px solid #6c757d",
    color: "#ffffff",
    fontSize: "12px",
    [theme.breakpoints.up('xl')]: {
      fontSize: "14px",
      marginTop: "-3.5rem",
      padding: "0.35rem 0.6rem",
    },
  },
  lobbyLeaderIcon: {
    color: '#E8A33D',
    fontSize: '1.2rem',
    filter: 'drop-shadow(0 2px 4px rgba(232, 163, 61, 0.6))',
    flexShrink: 0,
    [theme.breakpoints.up('xl')]: {
      fontSize: '1.4rem',
    },
  },
  playerAvatar: {
    width: '100%',
    height: '100%',
    margin: 0,
    borderRadius: '3px',
  },
  avatarWrapper: {
    position: 'relative',
    width: '6.5rem',
    height: '6.5rem',
    margin: '0 auto',
    flexShrink: 0,
    transition: 'all 0.3s ease',
    borderRadius: '4px',
    border: '1px solid rgba(232, 163, 61, 0.55)',
    overflow: 'hidden',
    [theme.breakpoints.up('xl')]: {
      width: '8rem',
      height: '8rem',
    },
  },
  avatarWrapperSelf: {
    border: '1px solid #E8A33D',
    boxShadow: '0 0 0 1px rgba(232, 163, 61, 0.25), 0 6px 16px rgba(0, 0, 0, 0.45)',
  },
  flagBadge: {
    position: 'absolute',
    right: '-3px',
    bottom: '-3px',
    width: '1.4rem',
    height: '1.4rem',
    borderRadius: '50%',
    border: '2px solid #1C1E20',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.45), 0 0 0 2px rgba(232, 163, 61, 0.25)',
    backgroundColor: '#1C1E20',
    objectFit: 'cover',
    [theme.breakpoints.up('xl')]: {
      width: '1.7rem',
      height: '1.7rem',
    },
  },
  countryCodeBadge: {
    position: 'absolute',
    right: '-2px',
    bottom: '-2px',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.1em',
    color: 'rgba(255,255,255,0.7)',
    backgroundColor: 'rgba(28, 30, 32, 0.92)',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: '3px',
    padding: '0.08rem 0.25rem',
    lineHeight: 1.2,
    [theme.breakpoints.up('xl')]: {
      fontSize: '0.7rem',
    },
  },
  eloContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  },
  statsRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.6rem',
    flexWrap: 'wrap',
    width: '100%',
    [theme.breakpoints.up('xl')]: {
      gap: '0.75rem',
    },
  },
  flipCardBackContent: {
    width: '100%',
    height: '100%',
    borderRadius: 4,
    position: 'relative',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    backgroundColor: '#1C1E20',
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 60px 40px -7px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    padding: '1.2rem',
    cursor: 'default',
  },
  containerBackButton: {
    width: '100%',
  },
  backBoxActions: {
    width: '100%',
    marginTop: '0.5rem',
    color: '#ffffff',
    fontSize: '0.8rem',
    padding: '0.5rem 0',
    textTransform: 'uppercase',
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    [theme.breakpoints.up('xl')]: {
      fontSize: '1rem',
      padding: '0.65rem 0',
      marginTop: '0.6rem',
    },
  },
  btnProfile: {
    backgroundColor: '#1E90FF',
    '&:hover': {
        backgroundColor: '#0073da',
        transform: 'scale(1.06)',
    }
  },
  btnAddFriend: {
    color: '#ffffff',
    backgroundColor: '#E8A33D',
    transition: 'transform 120ms ease, background-color 150ms ease, box-shadow 150ms ease',
    transform: 'translateZ(0)',
    willChange: 'transform',
    '&:hover': {
      backgroundColor: '#B97A1F',
      transform: 'scale(1.03)',
      boxShadow: '0 6px 20px rgba(232, 163, 61, 0.35)',
    },
    '&:active': {
      transform: 'scale(1.0)',
      boxShadow: '0 4px 14px rgba(232, 163, 61, 0.25)',
    },
    '&:focus-visible': {
      boxShadow: '0 0 0 2px rgba(255,255,255,0.18)',
    },
  },
  btnAlreadyFriend: {
    color: '#ffffff',
    backgroundColor: '#38b000',
    '&:hover': {
      backgroundColor: '#38b000',
      cursor: 'default',
    },
    cursor: 'default',
  },
  btnRequestSent: {
    color: '#ffffff',
    backgroundColor: '#808080',
    cursor: 'default',
    '&:hover': {
      backgroundColor: '#808080',
      transform: 'none',
      boxShadow: 'none',
      cursor: 'default',
    },
  },
  btnKick: {
    backgroundColor: '#A52A2A',
    '&:hover': {
        transform: 'scale(1.06)',
        backgroundColor: '#8B0000',
    }
  },
  offlineDivLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1C1E2070',
    zIndex: 10,
  },
  searchingDivLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1C1E2070',
    zIndex: 10,
  },
  containerStatusText: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.4rem 1.5rem',
    [theme.breakpoints.up('xl')]: {
      padding: '0.5rem 1.6rem',
    },
    background: '#e5e5e515',
    borderRadius: '4px',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    willChange: 'transform, opacity',
    opacity: 1,
    zIndex: 12,
  },
  containerStatusTextSlideIn: {
    animation: '$slideInFromLeft 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards',
  },
  containerStatusTextSlideOut: {
    animation: '$slideOutToLeft 0.4s cubic-bezier(0.55, 0.06, 0.68, 0.19) forwards',
  },
  statusText: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '1rem',
    [theme.breakpoints.up('xl')]: {
      fontSize: '1.2rem',
    },
    fontWeight: '525',
    gap: '0.5rem',
    color: '#cecece',
  },
  statusDot: {
    width: '0.8rem',
    height: '0.8rem',
    borderRadius: '50%',
    backgroundColor: '#cecece'
  },
  readyDivLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1C1E2070',
    zIndex: 11,
  },
  containerReadyText: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.5rem 2.4rem',
    [theme.breakpoints.up('xl')]: {
      padding: '0.6rem 2.8rem',
    },
    background: 'linear-gradient(135deg, #5a2d0d 0%, #c86200 50%, #d97000 100%)',
    borderRadius: '4px',
    border: '1px solid rgba(232, 163, 61, 0.3)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    boxShadow: `
      0 0 15px rgba(232, 163, 61, 0.35),
      0 0 30px rgba(232, 163, 61, 0.2),
      inset 0 0 15px rgba(255, 255, 255, 0.08),
      0 4px 12px rgba(0, 0, 0, 0.4)
    `,
    willChange: 'transform, opacity',
    opacity: 1,
    zIndex: 12,
    animation: '$readyGlow 2s ease-in-out infinite',
  },
  containerReadyTextSlideIn: {
    animation: '$slideInFromLeft 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards',
  },
  containerReadyTextSlideOut: {
    animation: '$slideOutToLeft 0.4s cubic-bezier(0.55, 0.06, 0.68, 0.19) forwards',
  },
  readyText: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '1rem',
    [theme.breakpoints.up('xl')]: {
      fontSize: '1.2rem',
    },
    fontWeight: '800',
    gap: '0.5rem',
    color: '#ffffff',
    textTransform: 'uppercase',
    textShadow: `
      0 0 8px rgba(232, 163, 61, 0.5),
      0 0 15px rgba(232, 163, 61, 0.3),
      0 2px 6px rgba(0, 0, 0, 0.6)
    `,
  },
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1,
      transform: 'scale(1)',
    },
    '50%': {
      opacity: 0.7,
      transform: 'scale(1.1)',
    },
  },
  '@keyframes slideInFromLeft': {
    '0%': {
      transform: 'translate(-150%, -50%)',
      opacity: 0,
    },
    '100%': {
      transform: 'translate(-50%, -50%)',
      opacity: 1,
    },
  },
  '@keyframes slideOutToLeft': {
    '0%': {
      transform: 'translate(-50%, -50%)',
      opacity: 1,
    },
    '100%': {
      transform: 'translate(-150%, -50%)',
      opacity: 0,
    },
  },
  '@keyframes readyGlow': {
    '0%, 100%': {
      boxShadow: `
        0 0 15px rgba(232, 163, 61, 0.35),
        0 0 30px rgba(232, 163, 61, 0.2),
        inset 0 0 15px rgba(255, 255, 255, 0.08),
        0 4px 12px rgba(0, 0, 0, 0.4)
      `,
      borderColor: 'rgba(232, 163, 61, 0.3)',
    },
    '50%': {
      boxShadow: `
        0 0 20px rgba(232, 163, 61, 0.45),
        0 0 40px rgba(232, 163, 61, 0.25),
        inset 0 0 18px rgba(255, 255, 255, 0.1),
        0 4px 14px rgba(0, 0, 0, 0.4)
      `,
      borderColor: 'rgba(232, 163, 61, 0.4)',
    },
  },
  founderChip: {
    height: "1.2rem",
    fontSize: "0.55rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    backgroundColor: FOUNDER_BADGE.bgColor,
    color: FOUNDER_BADGE.color,
    border: `1px solid ${FOUNDER_BADGE.borderColor}`,
    "& .MuiChip-label": {
      padding: "0 0.35rem",
    },
  },
  membershipChip: {
    height: "1.4rem",
    fontSize: "0.62rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    "& .MuiChip-label": {
      padding: "0 0.45rem",
    },
    [theme.breakpoints.up('xl')]: {
      height: "1.55rem",
      fontSize: "0.7rem",
    },
  },
  founderAvatarBadge: {
    position: "absolute",
    top: 0,
    left: 0,
    display: "flex",
    alignItems: "center",
    gap: "0.2rem",
    background: "linear-gradient(135deg, #FFE07A 0%, #FFD700 45%, #E8A33D 100%)",
    borderBottomRightRadius: "10px",
    padding: "0.2rem 0.5rem 0.24rem 0.4rem",
    zIndex: 2,
    cursor: "default",
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.5)',
    [theme.breakpoints.up('xl')]: {
      padding: "0.24rem 0.6rem 0.28rem 0.45rem",
    },
  },
  founderAvatarIcon: {
    color: "#1C1E20",
    fontSize: "0.85rem",
    [theme.breakpoints.up('xl')]: {
      fontSize: "0.95rem",
    },
  },
  founderAvatarText: {
    color: "#1C1E20",
    fontSize: "0.7rem",
    fontWeight: 800,
    letterSpacing: "0.3px",
    lineHeight: 1,
    [theme.breakpoints.up('xl')]: {
      fontSize: "0.8rem",
    },
  },
  badgeRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.3rem",
    flexWrap: "wrap",
    width: '100%',
    marginTop: '0.1rem',
    flexShrink: 0,
  },
  playerNameInner: {
    minWidth: 0,
    flex: 1,
    overflow: 'hidden',
  },
  playerNameText: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'center',
  },
}));

function resolveMembershipDisplay(player) {
  const tier = (player?.membershipTier || player?.membership?.tier || TIERS.FREE)
    .toString().trim().toLowerCase();
  if (tier === TIERS.FREE) return null;
  const display = TIER_DISPLAY[tier] || TIER_DISPLAY[TIERS.FREE];
  if (!display?.badge) return null;
  return { label: display.badge, color: display.color };
}

function resolveFounderBadge(player) {
  return player?.founderBadge?.granted ? player.founderBadge : null;
}

function renderCardFront({
  classes,
  isYou,
  isLobbyHost,
  player,
  avatarSrc,
  founderBadge,
  membershipDisplay,
  hasBadges,
  rating3Value,
  rating3SubRatings,
  impactRatingValue,
  impactRatingComponents,
  showSearchingLayer,
  showOfflineLayer,
  showStatus,
  statusAnimation,
}) {
  return (
    <>
      {showSearchingLayer && (
        <div
          className={
            isYou ? classes.searchingDivLayerIsYou : classes.searchingDivLayer
          }
        />
      )}
      {showOfflineLayer && <div className={classes.offlineDivLayer} />}
      <Typography className={classes.playerName}>
        {renderLobbyPlayerName({
          classes,
          isLobbyHost,
          nickname: player.nickname,
        })}
      </Typography>
      <div
        className={`${classes.avatarWrapper} ${
          isYou ? classes.avatarWrapperSelf : ''
        }`}
      >
        <Avatar
          variant="square"
          className={classes.playerAvatar}
          size="large"
          src={avatarSrc}
        />
        {renderCountryBadge({ classes, player })}
        {founderBadge && (
          <Tooltip title={FOUNDER_TOOLTIP_TEXT} placement="top" arrow>
            <div className={classes.founderAvatarBadge}>
              <StarIcon className={classes.founderAvatarIcon} />
              <span className={classes.founderAvatarText}>
                #{founderBadge.number || ''}
              </span>
            </div>
          </Tooltip>
        )}
      </div>
      {showStatus && (
        <div className={`${classes.containerStatusText} ${statusAnimation}`}>
          <Typography className={classes.statusText}>
            <div className={classes.statusDot} />
            Offline
          </Typography>
        </div>
      )}
      <div className={classes.eloContainer}>
        <EloBadge value={player.elo} size="small" />
      </div>
      {(rating3Value != null || impactRatingValue != null) && (
        <div className={classes.statsRow}>
          {rating3Value != null && (
            <Rating3Badge
              value={rating3Value}
              subRatings={rating3SubRatings}
              size="small"
              showLabel
              compact
            />
          )}
          {impactRatingValue != null && (
            <ImpactRatingBadge
              value={impactRatingValue}
              components={impactRatingComponents}
              size="small"
              showLabel
              compact
            />
          )}
        </div>
      )}
      {hasBadges && (
        <div className={classes.badgeRow}>
          {membershipDisplay && (
            <Chip
              className={classes.membershipChip}
              label={membershipDisplay.label}
              size="small"
              style={{
                backgroundColor: `${membershipDisplay.color}22`,
                color: membershipDisplay.color,
                border: `1px solid ${membershipDisplay.color}`,
              }}
            />
          )}
        </div>
      )}
    </>
  );
}

function renderLobbyPlayerName({ classes, isLobbyHost, nickname }) {
  return (
    <>
      {isLobbyHost && (
        <Tooltip
          title="Leader"
          classes={{ tooltip: classes.tooltipContent }}
        >
          <StarIcon className={classes.lobbyLeaderIcon} />
        </Tooltip>
      )}
      <span className={classes.playerNameInner}>
        <span className={classes.playerNameText} title={nickname}>
          {nickname}
        </span>
      </span>
    </>
  );
}

function renderCountryBadge({ classes, player }) {
  if (player?.countryCode) {
    return (
      <span className={classes.countryCodeBadge}>
        {player.countryCode.toString().trim().slice(0, 2).toUpperCase()}
      </span>
    );
  }

  if (player?.flagImageUrl) {
    return (
      <img className={classes.flagBadge} src={player.flagImageUrl} alt="" aria-hidden="true" />
    );
  }

  return null;
}

export default function LobbyPlayerBox({ 
  player = null, 
  isYou = false, 
  onKick, 
  friendlist = [], 
  handleSendRequestFriend,
  pendingRequests = [],
  lobbyState,
  currentLobbyCode,
  isCurrentUserLeader = false,
  canFlipCards = true,
  showUnavailableActionMessage,
  avatarCacheBust = 0,
}) {
    const classes = useStyles();
    const history = useHistory()
    const [flipCard, setFlipCard] = useState(false);
    const [showStatus, setShowStatus] = useState(false);
    const [statusAnimation, setStatusAnimation] = useState('');
    const [showReady, setShowReady] = useState(false);
    const [readyAnimation, setReadyAnimation] = useState('');
    
    const isLobbyHost = player && player.role === "host";
    const isSearching = lobbyState === "searching";
    // Show searching overlay when: solo searching OR (in lobby and cards blocked)
    const showSearchingLayer = isSearching || (!canFlipCards && currentLobbyCode);

    // Debug logging
    if (isYou && player) {
        console.log('🖼️ [LobbyPlayerBox] Player data received:', {
            userId: player.userId,
            nickname: player.nickname,
            hasAvatar: player.hasOwnProperty('avatar'),
            avatarValue: player.avatar,
            picture: player.picture,
            cacheBust: avatarCacheBust,
        });
    }

    const avatarVersion = pickAvatarUpdatedAtMs(
      player,
      isYou ? avatarCacheBust : null
    );
    const avatarSrc = resolveAvatarSrc({
      basePath,
      picture: player?.picture,
      userId: player?.userId || player?._id,
      avatarUpdatedAtMs: avatarVersion,
      hasCustomAvatar: !!player?.avatar,
    });
    
    if (isYou) {
        console.log('🖼️ [LobbyPlayerBox] Avatar URL decision:', {
            hasCustomAvatarPicture:
              player?.picture && player.picture.includes('/api/v1/media/avatar/'),
            avatarUpdatedAtMs: avatarVersion,
            finalAvatarSrc: avatarSrc,
        });
    }

    const isFriend = Array.isArray(friendlist)
      ? !!friendlist.find((friend) => friend && friend.friendSteamId === player?.steamId)
      : false;

    const friendRecord = Array.isArray(friendlist)
      ? friendlist.find((f) => f && String(f.friendSteamId) === String(player?.steamId))
      : undefined;

    const isPlayerOnline = player?.isOnline !== undefined 
      ? player.isOnline 
      : friendRecord?.friendData?.isOnline;

    const hasPendingRequest = Array.isArray(pendingRequests) ?
      pendingRequests.some((req) => String(req.toUser) === String(player?.steamId)) : false;

      useEffect(() => {
        if (isPlayerOnline === false) {
          setShowStatus(true);
        }
      }, []);

      useEffect(() => {
        if (isPlayerOnline === false && !showStatus) {
          setShowStatus(true);
          setStatusAnimation(classes.containerStatusTextSlideIn);
        } else if (isPlayerOnline === true && showStatus) {
          setStatusAnimation(classes.containerStatusTextSlideOut);
          setTimeout(() => {
            setShowStatus(false);
            setStatusAnimation('');
          }, 400);
        }
      }, [isPlayerOnline, showStatus, classes.containerStatusTextSlideIn, classes.containerStatusTextSlideOut]);

      useEffect(() => {
        if (isSearching && !showReady && isYou) {
          setShowReady(true);
          setReadyAnimation(classes.containerReadyTextSlideIn);
        } else if ((!isSearching || !isYou) && showReady) {
          setReadyAnimation(classes.containerReadyTextSlideOut);
          setTimeout(() => {
            setShowReady(false);
            setReadyAnimation('');
          }, 400);
        }
      }, [isSearching, showReady, isYou, classes.containerReadyTextSlideIn, classes.containerReadyTextSlideOut]);

    const handleProfile = () => {
      history.push(`/profile/${player._id}`);
    };

    const founderBadge = resolveFounderBadge(player);
    const membershipDisplay = resolveMembershipDisplay(player);
    const hasBadges = !!membershipDisplay;
    const rating3Value = player?.rating3?.current ?? player?.rating3;
    const rating3SubRatings = player?.rating3?.subRatings || player?.rating3Sub || null;
    const impactRatingValue = player?.impactRating?.current ?? null;
    const impactRatingComponents = player?.impactRating?.components || null;

    const cardFront = renderCardFront({
      classes,
      isYou,
      isLobbyHost,
      player,
      avatarSrc,
      founderBadge,
      membershipDisplay,
      hasBadges,
      rating3Value,
      rating3SubRatings,
      impactRatingValue,
      impactRatingComponents,
      showSearchingLayer,
      showOfflineLayer: !isYou && !isPlayerOnline,
      showStatus: !isYou && showStatus,
      statusAnimation,
    });

    if (isYou) {
      return (
        <div
          className={`${classes.playerCard} ${classes.playerCardSelf} ${classes.isYouBorder}`}
        >
          {cardFront}
        </div>
      );
    }

    return (
      <div className={classes.flipPlayerBox}>
        <div className={`${classes.flipInner} ${flipCard ? classes.rotate : ''}`}>
          <div className={`${classes.flipCardFront} ${flipCard ? classes.peNone : ''}`}>
            <div
              className={`${classes.playerCard} ${classes.hoverNotYouCard}`}
              onClick={() =>
                !canFlipCards ? showUnavailableActionMessage() : setFlipCard(true)
              }
            >
              {cardFront}
            </div>
          </div>
          <div className={`${classes.flipCardBack} ${!flipCard ? classes.peNone : ''}`}>
            <div className={`${classes.flipCardBackContent} ${classes.hoverNotYouCard}`} onClick={(e) => e.stopPropagation()}>
                <IconButton className={classes.closeFlipButton} size="small" onClick={() => setFlipCard(false)}>
                  <CloseIcon className={classes.closeFlipIcon} />
                </IconButton>
                <div className={classes.containerBackButton}>
                  <Button className={`${classes.backBoxActions} ${classes.btnProfile}`} onClick={handleProfile}>
                    Profile
                  </Button>
                </div>
                <div className={classes.containerBackButton}>
                  {isFriend ? (
                    <Button className={`${classes.backBoxActions} ${classes.btnAlreadyFriend}`}>
                      Friends
                    </Button>
                  ) : hasPendingRequest ? (
                    <Button className={`${classes.backBoxActions} ${classes.btnRequestSent}`} disabled>
                      Request sent
                    </Button>
                  ) : (
                    <Button
                      className={`${classes.backBoxActions} ${classes.btnAddFriend}`}
                      onClick={() => { handleSendRequestFriend(player.steamId); }}
                    >
                      Add Friend
                    </Button>
                  )}
                </div>
                {isCurrentUserLeader && (
                  <div className={classes.containerBackButton}>
                    <Button className={`${classes.backBoxActions} ${classes.btnKick}`} onClick={() => onKick(player)}>
                      Kick
                    </Button>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    );
}